import { useUser } from "src/contexts/user-context";
import {
  useActiveSupportAccessGrantsQuery,
  useIsAdminEntitlementRequiredQuery,
} from "src/graphql";
import {
  getActiveSupportAccessGrant,
  SupportAccessType,
  supportAccessTypesRequiringControl,
} from "src/components/support-access/utils";
import { useMemo } from "react";

export const useImpersonationPermissions = (
  impersonationType: "admin" | "viewer" | "readsupport" | "user_groups",
  userConfirmedMasterOverride: boolean,
) => {
  const { user, workspace } = useUser();
  const supportAccessType = workspace?.organization
    ?.support_access_type as SupportAccessType;

  // Check if admin workspace entitlement is required from backend config
  // Only applies to admin/write access, not viewer
  const { data: entitlementData } = useIsAdminEntitlementRequiredQuery({
    workspaceId: workspace?.id,
  });

  const isEntitlementRequired = useMemo(
    () =>
      entitlementData?.isAdminEntitlementRequired === true &&
      impersonationType !== "viewer" &&
      impersonationType !== "readsupport",
    [entitlementData, impersonationType],
  );

  const isGrantRequired = useMemo(
    () =>
      supportAccessType != null &&
      supportAccessTypesRequiringControl.includes(supportAccessType),
    [supportAccessType],
  );

  const {
    data: satisfiedByGrant,
    refetch: refetchGrant,
    isFetching: isFetchingGrant,
  } = useActiveSupportAccessGrantsQuery(undefined, {
    enabled: isGrantRequired,
    select: (data) =>
      getActiveSupportAccessGrant(data.support_access_grant, workspace?.id),
  });

  const isGrantRequiredForImpersonationType = useMemo(
    () =>
      isGrantRequired &&
      (supportAccessType === "read_write_grant_required" ||
        (supportAccessType === "write_grant_required" &&
          impersonationType !== "viewer")),
    [isGrantRequired, supportAccessType, impersonationType],
  );

  const isMasterOverrideAllowed = useMemo(
    () =>
      isGrantRequiredForImpersonationType &&
      !satisfiedByGrant &&
      user?.admin_type === "master",
    [isGrantRequiredForImpersonationType, user?.admin_type, satisfiedByGrant],
  );

  const disableSubmit = useMemo(() => {
    if (isFetchingGrant) return true;
    if (!isGrantRequiredForImpersonationType) return false;
    if (isMasterOverrideAllowed) return !userConfirmedMasterOverride;
    return !satisfiedByGrant;
  }, [
    isGrantRequiredForImpersonationType,
    isMasterOverrideAllowed,
    userConfirmedMasterOverride,
    isFetchingGrant,
    satisfiedByGrant,
  ]);

  return {
    isFetchingGrant,
    isGrantRequired,
    isEntitlementRequired,
    satisfiedByGrant,
    refetchGrant,
    isMasterOverrideAllowed,
    disableSubmit,
  };
};
